/**
 * Display-currency context.
 *
 * Amounts in the app are in the BUSINESS currency (the books' currency) —
 * cedis for a Ghana shop, naira in Lagos, shillings in Nairobi. That source
 * currency comes from the open business (/businesses/mine returns each
 * business's effective currency) and lives in lib/currency's store, so
 * code outside React formats with it too.
 *
 * The header switcher can show read-only views in another currency (e.g.
 * a group owner reading in USD). It never changes a ledger line. Tills,
 * receipts and inputs always use the business currency (formatMoney /
 * useCurrency), not this conversion.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useFxRates } from "@/hooks/useFxRates";
import useBusinessStore from "@/stores/businessStore";
import { currencyInfo, formatAmount, useCurrencyStore, type MoneyFormatOptions } from "@/lib/currency";

const STORAGE_KEY = "eaze:display-currency:v2";

export type CurrencyCode = string;

interface DisplayCurrencyContextValue {
  /** The open business's currency (what amounts are in). */
  sourceCurrency: CurrencyCode;
  /** What the user is viewing in (= source unless they switched). */
  displayCurrency: CurrencyCode;
  /** Pick a display currency; picking the business currency clears the override. */
  setDisplayCurrency: (c: CurrencyCode) => void;
  /** Currencies we can convert to (from FX rates), source first. */
  availableCurrencies: CurrencyCode[];
  /** Convert minor units in the source currency to a major-unit float in the display currency. */
  convert: (minorSource: number) => number;
  /** @deprecated use convert — kept for older callers. */
  convertFromGHS: (minorSource: number) => number;
  /** Convert AND format with the display currency's symbol. */
  format: (minorSource: number, opts?: MoneyFormatOptions) => string;
  ratesCachedAt: string | null;
  fxUnavailable: boolean;
}

export const DisplayCurrencyContext = createContext<DisplayCurrencyContextValue | null>(null);

export function currencySymbol(c: CurrencyCode): string {
  return currencyInfo(c).symbol;
}

/** Keeps lib/currency's store on the open business's currency. */
function useSyncBusinessCurrency(): string {
  const setCode = useCurrencyStore((s) => s.setCode);
  const code = useCurrencyStore((s) => s.code);
  const businesses = useBusinessStore((s) => s.availableBusinesses);
  const activeId = useBusinessStore((s) => s.activeBusinessId);
  const activeSlug = useBusinessStore((s) => s.activeBusinessSlug);
  const pageId = useBusinessStore((s) => s.pageBusinessId);
  const next = useMemo(() => {
    const list = (businesses || []) as { _id?: string; slug?: string; currency?: string; isDefault?: boolean }[];
    const active =
      list.find((b) => (activeId && b._id === activeId) || (activeSlug && b.slug === activeSlug)) ||
      // All businesses mode: the business the page is showing.
      (!activeSlug && pageId ? list.find((b) => b._id === pageId) : undefined) ||
      // Group view (no business open): the tenant's main business.
      list.find((b) => b.isDefault) ||
      list[0];
    return active?.currency ? currencyInfo(active.currency).code : null;
  }, [businesses, activeId, activeSlug, pageId]);
  useEffect(() => {
    if (next && next !== code) setCode(next);
  }, [next, code, setCode]);
  return next || code;
}

export const DisplayCurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const source = useSyncBusinessCurrency();
  const [override, setOverride] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const setDisplayCurrency = (c: CurrencyCode) => {
    const v = c === source ? null : c;
    setOverride(v);
    try {
      if (v) localStorage.setItem(STORAGE_KEY, v);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* non-fatal */
    }
  };

  // Rates are quoted against one base (GHS today: 1 GHS = rates[X] X). Any
  // pair converts through it: src → base → dst.
  const { data: fxData, isLoading } = useFxRates();

  const value = useMemo<DisplayCurrencyContextValue>(() => {
    const rates = fxData?.rates ?? null;
    const base = (fxData?.base || "GHS").toUpperCase();
    const rateOf = (c: string) => (c === base ? 1 : rates?.[c]);
    const canConvert = (c: string) => c === source || (!!rateOf(c) && !!rateOf(source));
    const wanted = override || source;
    const effective = canConvert(wanted) ? wanted : source;

    const convert = (minorSource: number) => {
      const major = minorSource / 100;
      if (effective === source) return major;
      return (major / (rateOf(source) as number)) * (rateOf(effective) as number);
    };
    const format = (minorSource: number, opts: MoneyFormatOptions = {}) =>
      formatAmount(convert(minorSource), effective, opts);

    const others = rates ? Object.keys(rates).filter((c) => c !== source && c !== base && canConvert(c)) : [];
    const availableCurrencies = [source, ...(base !== source && canConvert(base) ? [base] : []), ...others];

    return {
      sourceCurrency: source,
      displayCurrency: effective,
      setDisplayCurrency,
      availableCurrencies,
      convert,
      convertFromGHS: convert,
      format,
      ratesCachedAt: fxData?.cachedAt ?? null,
      fxUnavailable: !rates && !isLoading,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, override, fxData, isLoading]);

  return <DisplayCurrencyContext.Provider value={value}>{children}</DisplayCurrencyContext.Provider>;
};

export function useDisplayCurrency(): DisplayCurrencyContextValue {
  const ctx = useContext(DisplayCurrencyContext);
  if (!ctx) throw new Error("useDisplayCurrency must be used inside <DisplayCurrencyProvider>");
  return ctx;
}
